function nz(v) {
  return String(v ?? "").trim();
}

/**
 * Auth-User-ID des Trainer-Stammdatensatzes (RLS-Datenowner, nicht zwingend Login).
 *
 * @param {object|null|undefined} trainerRow
 */
export function resolveTrainerLinkedAuthUserId(trainerRow) {
  return nz(trainerRow?.created_by);
}

/**
 * Session liegt in der Eigenverantwortung des eingeloggten Trainers.
 * planned_by (auth.uid. beim Anlegen) ist maßgeblich — created_by ist nur RLS-Owner.
 *
 * @param {object|null|undefined} session
 * @param {object|null|undefined} trainerRow
 * @param {string} [viewerAuthUserId] Auth-UID des eingeloggten Trainer-Logins
 */
export function isSessionBillingTrainerSelfManaged(
  session,
  trainerRow,
  viewerAuthUserId = "",
) {
  const trainerId = nz(trainerRow?.id);
  if (!trainerId || nz(session?.trainer_id) !== trainerId) return false;

  const plannedBy = nz(session?.planned_by);
  const authUid = nz(viewerAuthUserId);

  if (plannedBy && authUid) {
    return plannedBy === authUid;
  }

  if (plannedBy && !authUid) {
    return false;
  }

  // Legacy ohne planned_by: nicht zuordenbar → Koordinator-Verantwortung
  return false;
}

/**
 * @param {object|null|undefined} session
 * @param {object|null|undefined} trainerRow
 * @param {string} [viewerAuthUserId]
 */
export function isSessionBillingCoordinatorManaged(
  session,
  trainerRow,
  viewerAuthUserId = "",
) {
  const trainerId = nz(trainerRow?.id);
  if (!trainerId || nz(session?.trainer_id) !== trainerId) return false;
  return !isSessionBillingTrainerSelfManaged(
    session,
    trainerRow,
    viewerAuthUserId,
  );
}

/**
 * @param {object[]} sessions
 * @param {object|null|undefined} trainerRow
 * @param {string} [viewerAuthUserId]
 */
export function trainerHasSelfManagedBillingSessions(
  sessions,
  trainerRow,
  viewerAuthUserId = "",
) {
  const list = Array.isArray(sessions) ? sessions : [];
  return list.some((s) =>
    isSessionBillingTrainerSelfManaged(s, trainerRow, viewerAuthUserId),
  );
}

/**
 * @param {Map<string, object>|object[]} trainersById
 */
function trainerRowById(trainersById, trainerId) {
  const tid = nz(trainerId);
  if (!tid) return null;
  if (trainersById instanceof Map) return trainersById.get(tid) || null;
  return (Array.isArray(trainersById) ? trainersById : []).find(
    (tr) => nz(tr?.id) === tid,
  );
}

/**
 * @param {object[]} sessions
 * @param {object|null|undefined} trainerRow
 * @param {string} [viewerAuthUserId]
 */
export function filterSessionsForTrainerSelfManagedBilling(
  sessions,
  trainerRow,
  viewerAuthUserId = "",
) {
  if (!trainerRow) return [];
  return (Array.isArray(sessions) ? sessions : []).filter((s) =>
    isSessionBillingTrainerSelfManaged(s, trainerRow, viewerAuthUserId),
  );
}

/**
 * Eigenplanung durch Trainer-Login (nicht Planner/Koordinator).
 * Für Koordinator-Filter ohne Viewer-Auth.
 */
export function isSessionTrainerSelfPlannedForBilling(session, trainerRow) {
  const plannedBy = nz(session?.planned_by);
  if (plannedBy) {
    const owner = nz(session?.created_by);
    if (owner && plannedBy !== owner) {
      const loginRole = nz(trainerRow?.login_role) || "trainer";
      if (loginRole !== "planner") return true;
    }
    return false;
  }
  return isSessionBillingTrainerSelfManaged(session, trainerRow);
}

/**
 * Koordinator-Rechnungswesen: Trainer-Eigenleistungen ausblenden.
 *
 * @param {object|null|undefined} session
 * @param {Map<string, object>|object[]} trainersById
 */
export function isSessionVisibleInCoordinatorBilling(session, trainersById) {
  const tid = nz(session?.trainer_id);
  if (!tid) return true;
  const trainerRow = trainerRowById(trainersById, tid);
  if (!trainerRow) return true;
  return !isSessionTrainerSelfPlannedForBilling(session, trainerRow);
}
